/**
 * @file src/api/platformEventsClient.js
 * @description Transport helpers for the public Platform Events collector.
 */

import { resolveApiBase } from './httpClient.js';
// The PASSIVE token read — never loads the SDK or runs the handshake; see its own doc for why
// that matters on this every-visitor path.
import { getCurrentAuthToken } from '../auth/viewerAuth.js';
import { buildOfflineWriteResponse, isOfflineReadOnly } from '../offline/offlineMode.js';

const PLATFORM_EVENTS_PATH = '/platform-events';

/**
 * Returns the frontend collector endpoint for usage events.
 * Follows the same API base/version resolution used by the rest of Orbiters.
 * @returns {string|null}
 */
export function getPlatformEventsEndpoint() {
  const base = resolveApiBase();
  if (!base) {
    return null;
  }
  return `${base}${PLATFORM_EVENTS_PATH}`;
}

/**
 * Posts a batch of platform-events in a single request.
 * @param {object[]} events
 * @param {{ keepalive?: boolean, signal?: AbortSignal }} [options]
 * @returns {Promise<Response>}
 */
export async function postPlatformEvents(events, { keepalive = false, signal } = {}) {
  // This client posts with a raw `fetch` rather than through `fetchJsonFromApi`, so the offline
  // door there does not cover it and it needs its own. Refusing here rather than letting the POST
  // fail is the difference between "the platform has no record of this session" — which is true —
  // and a queue of events describing an offline session as though it had been observed.
  if (isOfflineReadOnly()) {
    return buildOfflineWriteResponse();
  }

  const endpoint = getPlatformEventsEndpoint();
  if (!endpoint) {
    throw new Error('[PlatformEvents] API base URL not configured.');
  }

  const token = await getCurrentAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetch(endpoint, {
    method: 'POST',
    credentials: 'omit',
    headers,
    body: JSON.stringify(events),
    keepalive,
    signal,
  });
}
